import React from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Heart, Shield, ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import { useReveal } from "../lib/motion";

const topics = [
    { tag: "Trying to Conceive", count: 12483, color: "#E91E8C" },
    { tag: "PCOS Support",       count:  8921, color: "#9333A0" },
    { tag: "Pregnancy",          count: 15782, color: "#EC4899" },
    { tag: "Postpartum",         count:  6204, color: "#F472B6" },
    { tag: "Endometriosis",      count:  4318, color: "#3B0764" },
    { tag: "Mental Wellness",    count:  9876, color: "#E91E8C" },
];

const posts = [
    { tag: "Trying to Conceive", title: "Second IVF cycle starting — looking for buddies", by: "anonymous_orchid", replies: 47 },
    { tag: "PCOS Support",       title: "Diet changes that finally regulated my cycle",     by: "moonbeam_88",      replies: 132 },
    { tag: "Pregnancy",          title: "Week 22 — first kicks!!",                          by: "future_mom_a",     replies: 89 },
    { tag: "Mental Wellness",    title: "Luteal phase anxiety — how do you cope?",          by: "calmwaves",        replies: 56 },
];

const PILLARS = [
    { icon: Shield,        title: "Safe & moderated",   text: "Every post is reviewed by trained moderators. Zero tolerance for shaming or misinformation." },
    { icon: Heart,         title: "Anonymous if needed", text: "Post anonymously about sensitive topics. Your real identity is never shown." },
    { icon: MessageCircle, title: "Expert Q&A weekly",  text: "OB-GYNs and certified coaches answer your questions every Wednesday." },
];

export default function Community() {
    useReveal();
    return (
        <div data-testid="community-page">
            <PageHero
                testId="community-hero"
                overline="The Community"
                title={
                    <>
                        You are <span className="italic font-light">never</span> alone.
                    </>
                }
                subtitle="A private, moderated space where women share experiences, ask questions, and support each other through every stage of life. Anonymous posting available."
                size="lg"
            >
                <Link to="/pricing" className="btn-primary magnet" data-testid="community-cta">
                    Join the community
                    <ArrowRight className="w-4 h-4" />
                </Link>
            </PageHero>

            {/* HERO PHOTO — warm community band */}
            <section className="max-w-6xl mx-auto px-6 lg:px-12 -mt-4">
                <div className="relative rounded-[1.75rem] overflow-hidden shadow-[0_25px_60px_-30px_rgba(168,122,166,0.35)] border-4 border-white/70 reveal">
                    <img
                        src="/community-photo.jpg"
                        alt="Women together at sunset — the Femcare community"
                        loading="lazy"
                        className="w-full h-[260px] md:h-[340px] object-cover"
                        data-testid="community-hero-photo"
                    />
                    <div className="absolute inset-0" style={{background:"linear-gradient(180deg,transparent 50%,rgba(42,31,50,0.35) 100%)"}} />
                    <p className="absolute bottom-5 left-6 md:left-8 text-white font-serif italic text-lg md:text-xl max-w-sm">
                        Every woman deserves someone who understands.
                    </p>
                </div>
            </section>

            {/* TOPICS BENTO */}
            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
                <h2 className="editorial-headline text-3xl md:text-4xl text-femcare-purple mb-8 reveal">
                    Trending topics
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {topics.map((t, i) => (
                        <div
                            key={t.tag}
                            className={`bento-tile p-5 cursor-pointer reveal reveal-delay-${(i % 4) + 1}`}
                            data-testid={`topic-${t.tag.replace(/ /g,'-')}`}
                        >
                            <div className="w-2 h-2 rounded-full mb-3" style={{ backgroundColor: t.color }} />
                            <p className="text-sm font-medium text-femcare-purple">{t.tag}</p>
                            <p className="text-xs text-femcare-purple/50 mt-1">{t.count.toLocaleString()} posts</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* FORUM */}
            <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-16">
                <h2 className="editorial-headline text-3xl md:text-4xl text-femcare-purple mb-8 reveal">
                    Latest from the forum
                </h2>
                <div className="card-editorial overflow-hidden divide-y divide-femcare-border reveal reveal-delay-1">
                    {posts.map((p, i) => (
                        <div key={i} className="p-6 hover:bg-femcare-petal/30 transition flex items-start gap-4">
                            <div className="w-10 h-10 rounded-full bg-femcare-pink/15 flex items-center justify-center shrink-0">
                                <MessageCircle className="w-5 h-5 text-femcare-pink" />
                            </div>
                            <div className="flex-1">
                                <p className="text-xs uppercase tracking-[0.2em] text-femcare-accent mb-1">{p.tag}</p>
                                <h3 className="font-serif text-xl text-femcare-purple mb-1">{p.title}</h3>
                                <p className="text-xs text-femcare-purple/50">by {p.by} · {p.replies} replies</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* PILLARS */}
            <section className="bg-femcare-petal/40 py-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {PILLARS.map((c, i) => (
                            <div
                                key={c.title}
                                className={`card-editorial p-8 reveal reveal-delay-${i + 1}`}
                            >
                                <c.icon className="w-6 h-6 text-femcare-pink mb-4" />
                                <h3 className="font-serif text-2xl text-femcare-purple mb-3">{c.title}</h3>
                                <p className="text-sm text-femcare-purple/70 leading-relaxed">{c.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
